import React, { useState, useEffect, useRef } from "react";
import { View, TouchableOpacity, Modal, StyleSheet } from "react-native";
import { Text } from "../../src/components";

type Props = {
  date: Date;
  onDateChange?: (date: Date) => void;
  onConfirm?: (date: Date) => void;
  onCancel?: () => void;
  mode?: "date" | "time" | "datetime";
  modal?: boolean;
  open?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  fitParent?: boolean;
};

const DatePicker: React.FC<Props> = ({ 
  date, 
  onDateChange, 
  onConfirm,
  onCancel,
  mode = "date",
  modal = false,
  open = false,
  minimumDate,
  maximumDate,
  title,
  confirmText = "Confirmer",
  cancelText = "Annuler",
  fitParent = false
}) => {
  const [selectedDate, setSelectedDate] = useState(date);
  const [selectedTime, setSelectedTime] = useState({
    hours: date.getHours(),
    minutes: date.getMinutes()
  });

  useEffect(() => {
    setSelectedDate(date);
    setSelectedTime({
      hours: date.getHours(),
      minutes: date.getMinutes()
    });
  }, [date]);

  const handleDateChange = (newDate: Date) => {
    setSelectedDate(newDate);
    onDateChange?.(newDate);
  };

  const handleTimeChange = (hours: number, minutes: number) => {
    const newTime = { hours, minutes };
    setSelectedTime(newTime);
    
    const newDateTime = new Date(selectedDate);
    newDateTime.setHours(newTime.hours, newTime.minutes);
    onDateChange?.(newDateTime);
  };

  const handleConfirm = () => {
    const finalDate = new Date(selectedDate);
    if (mode === "time" || mode === "datetime") {
      finalDate.setHours(selectedTime.hours, selectedTime.minutes);
    }
    onConfirm?.(finalDate);
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const formatTime = (hours: number, minutes: number) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const renderDateInput = () => {
    return (
      <input
        type="date"
        value={selectedDate.toISOString().split("T")[0]}
        onChange={(e) => {
          // Handle clear button click - close modal instead of creating invalid date
          if (!e.target.value) {
            return;
          }
          handleDateChange(new Date(e.target.value));
        }}
        min={minimumDate?.toISOString().split("T")[0]}
        max={maximumDate?.toISOString().split("T")[0]}
        style={styles.dateInput}
      />
    );
  }

  const timeInputRef = useRef<HTMLInputElement>(null);

  const renderTimeInput = () => (
    <View style={styles.timeContainer}>
      <input
        ref={timeInputRef}
        type="time"
        value={formatTime(selectedTime.hours, selectedTime.minutes)}
        onInput={(e) => {
          // onInput fires as user types, allowing us to capture changes before browser auto-focuses
          const target = e.target as HTMLInputElement;
          if (!target.value) {
            return;
          }
          const [hours, minutes] = target.value.split(':').map(Number);
          // Update both hours and minutes together in a single state update
          handleTimeChange(hours, minutes);
        }}
        onChange={(e) => {
          // onChange as fallback for when onInput doesn't fire
          const target = e.target as HTMLInputElement;
          if (!target.value) {
            return;
          }
          const [hours, minutes] = target.value.split(':').map(Number);
          handleTimeChange(hours, minutes);
        }}
        style={styles.timeInput}
      />
    </View>
  );

  const renderModalContent = () => fitParent ? (open ? (
    <View style={{position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, height: '100%', backgroundColor: '#5598D230', justifyContent: 'center', alignItems: 'center'}} 
      onStartShouldSetResponder={() => true}
      onResponderGrant={(evt) => {
        // Check if the touch is on the backdrop (not on modal content)
        if (evt.target === evt.currentTarget) {
          handleCancel();
        }
      }
    }>
      <View style={styles.modalContent}>
        {title && <Text type="headline" style={styles.modalTitle}>{title}</Text>}
        
        <View style={styles.pickerContainer}>
          {mode === "date" && renderDateInput()}
          {mode === "time" && renderTimeInput()}
          {mode === "datetime" && (
            <View style={styles.datetimeContainer}>
              {renderDateInput()}
              {renderTimeInput()}
            </View>
          )}
        </View>

        <View style={styles.buttonContainer}>
          <TouchableOpacity style={styles.cancelButton} onPress={handleCancel}>
            <Text type="body" color="#666">{cancelText}</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.confirmButton} onPress={handleConfirm}>
            <Text type="body" color="#007AFF">{confirmText}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  ) : <></>) :  (
    <Modal
      visible={open}
      transparent
      animationType="fade"
      onRequestClose={handleCancel}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          {title && <Text type="headline" style={styles.modalTitle}>{title}</Text>}
          
          <View style={styles.pickerContainer}>
            {mode === "date" && renderDateInput()}
            {mode === "time" && renderTimeInput()}
            {mode === "datetime" && (
              <View style={styles.datetimeContainer}>
                {renderDateInput()}
                {renderTimeInput()}
              </View>
            )}
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity style={styles.cancelButton} onPress={handleCancel}>
              <Text type="body" color="#666">{cancelText}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.confirmButton} onPress={handleConfirm}>
              <Text type="body" color="#007AFF">{confirmText}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  if (modal) {
    return renderModalContent();
  }

  // Non-modal mode - render inline picker
  return (
    <View style={styles.inlineContainer}>
      {mode === "date" && renderDateInput()}
      {mode === "time" && renderTimeInput()}
      {mode === "datetime" && (
        <View style={styles.datetimeContainer}>
          {renderDateInput()}
          {renderTimeInput()}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 20,
  },
  modalTitle: {
    textAlign: 'center',
    marginBottom: 20,
  },
  pickerContainer: {
    marginBottom: 20,
  },
  datetimeContainer: {
    gap: 15,
  },
  timeContainer: {
    marginTop: 10,
  },
  dateInput: {
    padding: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'system-ui',
  },
  timeInput: {
    alignSelf: 'center',
    padding: 12,
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    fontSize: 16,
    fontFamily: 'system-ui',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 15,
  },
  cancelButton: {
    flex: 1,
    padding: 12,
    alignItems: 'center',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  confirmButton: {
    flex: 1,
    padding: 12,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: '#f0f8ff',
  },
  inlineContainer: {
    width: '100%',
  },
});

export default DatePicker;
